import React, { createContext, useContext, useState, useCallback } from 'react';
import {
  fetchOpeningHours,
  addOpeningHours,
  updateOpeningHours,
  deleteOpeningHours
} from '../lib/apiClientOpeningHours';

const OpeningHoursContext = createContext();

export const useOpeningHoursContext = () => useContext(OpeningHoursContext);

export const OpeningHoursProvider = ({ children }) => {
  const [openingHours, setOpeningHours] = useState(null); // Using 'null' as the initial state since it's a single object
  const [alert, setAlert] = useState({ message: '', severity: '' });

  // Fetch opening hours for a business
  const fetchOpeningHoursForBusinesForDate = useCallback(async (businessId, date) => {
    try {
      const response = await fetchOpeningHours(businessId, date);
      setOpeningHours(response);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setOpeningHours("NOT_FOUND");
      } else {
        setOpeningHours(null);
      }
    }
  }, []);

  // Add new opening hours
  const addNewOpeningHours = useCallback(async (businessId, openingHoursDetails) => {
    try {
      await addOpeningHours(businessId, openingHoursDetails);
      setAlert({ message: 'Opening hours added successfully!', severity: 'success' });
      await fetchOpeningHoursForBusinesForDate(businessId); // Refresh the opening hours
    } catch (error) {
      setAlert({ message: 'Failed to add opening hours.', severity: 'error' });
      throw error;
    }
  }, [fetchOpeningHoursForBusinesForDate]);

  // Update existing opening hours
  const updateExistingOpeningHours = useCallback(async (businessId, openingHoursDetails) => {
    try {
      await updateOpeningHours(businessId, openingHoursDetails);
      setAlert({ message: 'Opening hours updated successfully!', severity: 'success' });
      await fetchOpeningHoursForBusinesForDate(businessId); // Refresh the opening hours
    } catch (error) {
      setAlert({ message: 'Failed to update opening hours.', severity: 'error' });
      throw error;
    }
  }, [fetchOpeningHoursForBusinesForDate]);

  // Delete opening hours
  const deleteExistingOpeningHours = useCallback(async (businessId) => {
    try {
      await deleteOpeningHours(businessId);
      setAlert({ message: 'Opening hours deleted successfully!', severity: 'success' });
      setOpeningHours(null); // Clear the opening hours after deletion
    } catch (error) {
      setAlert({ message: 'Failed to delete opening hours.', severity: 'error' });
      throw error;
    }
  }, []);

  const contextValue = {
    openingHours,
    fetchOpeningHoursForBusinesForDate,
    addNewOpeningHours,
    updateExistingOpeningHours,
    deleteExistingOpeningHours,
    alert,
  };

  return (
    <OpeningHoursContext.Provider value={contextValue}>
      {children}
    </OpeningHoursContext.Provider>
  );
};